import { getSubjectLabels } from '@/lib/settings';
import { useEffect, useState } from 'react';
import { Bot, KeyRound, RefreshCw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { aiSettingsApi, type AiSettingsDto } from '../../../lib/api';

const defaultSettings: AiSettingsDto = {
  enabled: true,
  model: 'claude-sonnet-5-5',
  systemPrompt: '',
  guestSystemPrompt: '',
  authenticatedSystemPrompt: '',
  guestGreeting: '',
  authenticatedGreeting: '',
  chatLoggingEnabled: true,
  chatRetentionDays: 90,
  chatPseudonymizationMode: 'delete',
  apiKey: '',
  apiKeyIsSet: false,
  availableModels: ['claude-sonnet-5-5'],
  availableChatPseudonymizationModes: ['delete', 'mask'],
};

export default function AiSettingsPage() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<AiSettingsDto>(defaultSettings);

  const load = async () => {
    setLoading(true);
    try {
      const data = await aiSettingsApi.get();
      setSettings({
        ...defaultSettings,
        ...data,
        availableModels: Array.isArray(data.availableModels) && data.availableModels.length > 0
          ? data.availableModels
          : defaultSettings.availableModels,
      });
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'KI-Einstellungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Kein Schlüssel im Payload: Er kommt aus der Serverkonfiguration (T-0198).
      const payload: AiSettingsDto = { ...settings, apiKey: undefined };
      const saved = await aiSettingsApi.update(payload);
      setSettings({
        ...settings,
        ...saved,
      });
      toast.success('KI-Einstellungen wurden gespeichert');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">KI-Einstellungen</h1>
        <p className="text-muted-foreground">
          Systemprompt, Modell, Begrüßungen und Protokollregeln für den Website-Chat und den KI-Assistenten verwalten
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Chatbot-Konfiguration
          </CardTitle>
          <CardDescription>
            Der API-Key bleibt serverseitig. Im Frontend wird er nur maskiert dargestellt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <div className="text-sm font-medium">Chatbot aktiv</div>
              <div className="text-xs text-muted-foreground">Aktiviert den Chatbot auf der Website</div>
            </div>
            <Switch
              checked={!!settings.enabled}
              onCheckedChange={(v) => setSettings((prev) => ({ ...prev, enabled: v }))}
              disabled={loading || saving}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Modell</Label>
              <Select
                value={settings.model}
                onValueChange={(v) => setSettings((prev) => ({ ...prev, model: v }))}
                disabled={loading || saving}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Modell auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {(settings.availableModels || defaultSettings.availableModels || []).map((model) => (
                    <SelectItem key={model} value={model}>{model}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <KeyRound className="h-4 w-4" />
                Zugang zum KI-Dienst (Anthropic)
              </Label>
              <p className="rounded-md border px-3 py-2 text-sm">
                {settings.apiKeyIsSet ? 'Zugangsschlüssel ist hinterlegt' : 'Kein Zugangsschlüssel hinterlegt'}
              </p>
              <p className="text-xs text-muted-foreground">
                Der Schlüssel steht in der Serverkonfiguration (<code>ANTHROPIC_API_KEY</code>) und wird hier
                nicht angezeigt oder gespeichert.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Globaler Systemprompt</Label>
            <Textarea
              value={settings.systemPrompt}
              onChange={(e) => setSettings((prev) => ({ ...prev, systemPrompt: e.target.value }))}
              placeholder="Grundregeln und Stil des Assistenten..."
              disabled={loading || saving}
            />
          </div>

          <div className="space-y-2">
            <Label>Zusätzlicher Prompt für Gäste</Label>
            <Textarea
              value={settings.guestSystemPrompt}
              onChange={(e) => setSettings((prev) => ({ ...prev, guestSystemPrompt: e.target.value }))}
              placeholder="Optional: Regeln für nicht eingeloggte Besucher..."
              disabled={loading || saving}
              density="compact"
            />
          </div>

          <div className="space-y-2">
            <Label>Zusätzlicher Prompt für eingeloggte Nutzer</Label>
            <Textarea
              value={settings.authenticatedSystemPrompt}
              onChange={(e) => setSettings((prev) => ({ ...prev, authenticatedSystemPrompt: e.target.value }))}
              placeholder="Optional: Regeln für personalisierte Antworten..."
              disabled={loading || saving}
              density="compact"
            />
          </div>

          <div className="space-y-2">
            <Label>Begrüßung für Gäste</Label>
            <Textarea
              value={settings.guestGreeting || ''}
              onChange={(e) => setSettings((prev) => ({ ...prev, guestGreeting: e.target.value }))}
              placeholder="Begrüßungstext beim Öffnen des Chats für Gäste..."
              disabled={loading || saving}
              density="compact"
            />
          </div>

          <div className="space-y-2">
            <Label>Begrüßung für eingeloggte Nutzer</Label>
            <Textarea
              value={settings.authenticatedGreeting || ''}
              onChange={(e) => setSettings((prev) => ({ ...prev, authenticatedGreeting: e.target.value }))}
              placeholder="Begrüßungstext beim Öffnen des Chats für eingeloggte Nutzer..."
              disabled={loading || saving}
              density="compact"
            />
          </div>

          <div className="rounded-md border p-4 space-y-4">
            <div className="text-sm font-medium">Konversations-Tracking & Datenschutz</div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">Chat-Logging aktiv</div>
                <div className="text-xs text-muted-foreground">Speichert Konversationen für Auswertung im Admin-Bereich.</div>
              </div>
              <Switch
                checked={!!settings.chatLoggingEnabled}
                onCheckedChange={(v) => setSettings((prev) => ({ ...prev, chatLoggingEnabled: v }))}
                disabled={loading || saving}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Aufbewahrung (Tage)</Label>
                <Input
                  type="number"
                  min={1}
                  max={3650}
                  value={settings.chatRetentionDays ?? 90}
                  onChange={(e) => setSettings((prev) => ({ ...prev, chatRetentionDays: Number(e.target.value || 90) }))}
                  disabled={loading || saving}
                />
              </div>
              <div className="space-y-2">
                <Label>Ablauf nach Frist</Label>
                <Select
                  value={settings.chatPseudonymizationMode || 'delete'}
                  onValueChange={(v: 'delete' | 'mask') => setSettings((prev) => ({ ...prev, chatPseudonymizationMode: v }))}
                  disabled={loading || saving}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Modus auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="delete">Löschen</SelectItem>
                    <SelectItem value="mask">Inhalte maskieren</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={load} disabled={loading || saving}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Neu laden
            </Button>
            <Button onClick={save} disabled={loading || saving}>
              {saving ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Speichern
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Verfügbare Prompt-Variablen</CardTitle>
          <CardDescription>
            Diese Platzhalter kannst du in allen Systemprompt-Feldern nutzen. Sie werden serverseitig vor der Anfrage an das Modell ersetzt, auch in den Begrüßungen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="rounded-md border p-3"><code>{'{{musikschule_name}}'}</code><div className="text-muted-foreground mt-1">Name der Musikschule</div></div>
            <div className="rounded-md border p-3"><code>{'{{heutiges_datum}}'}</code><div className="text-muted-foreground mt-1">Heutiges Datum (YYYY-MM-DD)</div></div>
            <div className="rounded-md border p-3"><code>{'{{standorte_liste}}'}</code><div className="text-muted-foreground mt-1">Standorte als Bullet-Liste</div></div>
            <div className="rounded-md border p-3"><code>{'{{faecher_liste}}'}</code><div className="text-muted-foreground mt-1">{getSubjectLabels().plural} als Bullet-Liste</div></div>
            <div className="rounded-md border p-3"><code>{'{{standorte_json}}'}</code><div className="text-muted-foreground mt-1">Standorte als JSON</div></div>
            <div className="rounded-md border p-3"><code>{'{{faecher_json}}'}</code><div className="text-muted-foreground mt-1">{getSubjectLabels().plural} als JSON</div></div>
            <div className="rounded-md border p-3"><code>{'{{standorte_faecher_liste}}'}</code><div className="text-muted-foreground mt-1">Standort-{getSubjectLabels().plural}-Zuordnung als Liste</div></div>
            <div className="rounded-md border p-3"><code>{'{{standorte_faecher_json}}'}</code><div className="text-muted-foreground mt-1">Standort-{getSubjectLabels().plural}-Zuordnung als JSON</div></div>
            <div className="rounded-md border p-3"><code>{'{{user_name}}'}</code><div className="text-muted-foreground mt-1">Vollname (nur eingeloggt)</div></div>
            <div className="rounded-md border p-3"><code>{'{{user_first_name}}'}</code><div className="text-muted-foreground mt-1">Vorname (nur eingeloggt)</div></div>
            <div className="rounded-md border p-3"><code>{'{{user_last_name}}'}</code><div className="text-muted-foreground mt-1">Nachname (nur eingeloggt)</div></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
